// MainPage.jsx
import React, { useState, useEffect } from 'react';
import { Mail } from 'lucide-react';

// Design styles config
import { styles, designTokens } from './MainPage.styles';

// Layout Structural Subcomponents
import Header from './Header';
import Hero from './Hero';
import PolicySimulator from './PolicySimulator';
import RegistrationForm from './RegistrationForm';
import MetricsSection from './MetricsSection';
import ScrollToTop from './ScrollToTop';
import FAQSection from './FAQSection';

// Global Shared Core Atomic Elements 
import { 
  SectionHeader, InfoCard, HowStepCard, AccordionItem 
} from './SharedComponents';

export default function MainPage() {

  const [hoveredCardIndex, setHoveredCardIndex] = useState(null);
  const [activeSection, setActiveSection] = useState('home');
  const [scrolled, setScrolled] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [hoveredProblemIndex, setHoveredProblemIndex] = useState(null);
  
  const [simulatedFacility, setSimulatedFacility] = useState('TX-DOC');
  const [simulatedBinding, setSimulatedBinding] = useState('Hardcover');
  const [simulatedResult, setSimulatedResult] = useState({ passed: false, reason: '' });

  // Core Simulator Validation Thread Loop
  useEffect(() => {
    if (simulatedBinding === 'Softcover') {
      setSimulatedResult({ 
        passed: true, 
        reason: 'Properties clear. Order verified compliant and safe for standard shipping routes.' 
      });
    } else {
      if (simulatedFacility === 'TX-DOC') {
        setSimulatedResult({ 
          passed: false, 
          reason: 'TX-DOC Policy Sec 4.2 explicitly restricts hardcover components. Packaging halted.' 
        });
      } else {
        setSimulatedResult({ 
          passed: false, 
          reason: 'NY-DOCCS requires authorized facility vendor clearance for hardback catalogs. Order flagged.' 
        });
      }
    }
  }, [simulatedFacility, simulatedBinding]);

  // Document Intersection Observer Scroll Registry Loop
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      const sections = ['home', 'problem', 'features', 'how', 'simulator', 'faq', 'apply'];
      const scrollPosition = window.scrollY + 140;

      for (const section of sections) {
        const el = document.getElementById(section);
        if (el && scrollPosition >= el.offsetTop && scrollPosition < el.offsetTop + el.offsetHeight) {
          setActiveSection(section);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 70;
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
      setActiveSection(id);
    }
  };

  // Lightweight count-up animator component
  function AnimatedCounter({ targetValue, duration = 1500 }) {
    const [count, setCount] = React.useState(0);

    React.useEffect(() => {
      // Parse numeric value out of strings like "1,800+" or "50"
      const finalNumber = parseInt(String(targetValue).replace(/[^0-9]/g, ''), 10);
      
      if (isNaN(finalNumber) || finalNumber === 0) {
        setCount(0);
        return;
      }

      let startTime = null;
      const step = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);
        
        // Smooth cubic-bezier easeOut effect for slowing down as it reaches the target
        const easeProgress = 1 - Math.pow(1 - progress, 3);
        
        setCount(Math.floor(easeProgress * finalNumber));

        if (progress < 1) {
          window.requestAnimationFrame(step);
        }
      };

      window.requestAnimationFrame(step);
    }, [targetValue, duration]);

    // Format the number back with commas if it hits thousands, and re-append strings (+ / %)
    const formatValue = () => {
      const isPlus = String(targetValue).includes('+');
      const formattedNum = count.toLocaleString();
      return `${formattedNum}${isPlus ? '+' : ''}`;
    };

    return <>{formatValue()}</>;
  }

  return (
    <div style={styles.wrapper}>
      <link href="https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;600;700;800&family=Barlow:wght@400;500;600&display=swap" rel="stylesheet" />
      
      <Header scrolled={scrolled} activeSection={activeSection} scrollToSection={scrollToSection} />
      
      <Hero scrollToSection={scrollToSection} />

      {/* METRICS DISPATCH COMPONENT ROW */}
      <MetricsSection />

      {/* MARKET PROBLEM SECTION */}
      <section id="problem" style={styles.problemSection}>
        <div style={styles.sectionInner}>
          <SectionHeader 
            labelText="The Problem"
            title="What Facilities Deal With Every Day"
            description="Correctional facilities spend thousands of hours annually managing incoming book mail — rejecting packages, processing returns, and dealing with policy violations."
            lightMode={true}
          />
          <div style={styles.problemGrid}>
            <InfoCard iconKey="packageX" iconBg="rgba(239,68,68,0.08)" title="Rejected Packages" description="Families send books that violate facility policies. Mailroom staff spend hours processing rejections and sending packages back — time that could be spent on security." />
            <InfoCard iconKey="shieldAlert" iconBg="rgba(245,158,11,0.08)" title="Contraband Risk" description="Books from unverified sources create security risks. Hardbacks, certain genres, and unauthorized publishers bypass facility content restrictions." />
            <InfoCard iconKey="heartCrack" iconBg="rgba(34,197,148,0.08)" title="Family Frustration" description="Families waste money on books that get rejected. They don't know the rules. Inmates never receive books they need. Everyone loses." />
          </div>
        </div>
      </section>

      {/* COMPLIANCE PIPELINE FEATURES SECTION */}
     <section id="features" style={{ ...styles.problemSection, backgroundColor: designTokens.steel, borderTop: 'none' }}>
       <div style={styles.sectionInner}>
         <SectionHeader 
           labelText="The Solution"
           title="Engineered Pre-Flight Verification"
           description="Our structural configuration layers evaluate metadata vectors explicitly long before inventory leaves fulfillment centers."
           lightMode={false} 
         />
         
         <div style={styles.problemGrid}>
           {[
             {
               iconKey: "search",
               iconBg: "rgba(20,184,166,0.15)",
               iconEmoji: "🔍",
               title: "Granular Rules Parsing",
               description: "Systematically checks strict physical attributes, mapping softcover dimensions, bound properties, and item weights against facility records automatically."
             },
             {
               iconKey: "layers",
               iconBg: "rgba(168,85,247,0.15)",
               iconEmoji: "🥞",
               title: "Pre-Flight Infrastructure",
               description: "Orders evaluate programmatic target validation parameters instantly upon receipt to secure delivery pipelines proactively."
             },
             {
               iconKey: "sparkles",
               iconBg: "rgba(46,134,193,0.15)",
               iconEmoji: "✨",
               title: "Automated Interception",
               description: "Non-compliant components flag user accounts dynamically at source checkout, deflecting processing burdens completely from the facility boundaries."
             }
           ].map((card, index) => {
             // Evaluate active item hover calculation
             const isCurrentHovered = hoveredCardIndex === index;

             // Dynamic inline style calculation
             const cardStyle = {
               backgroundColor: '#112235', // Match a clean dark tint to sit on steel background
               borderRadius: '6px',
               padding: '32px 28px',
               borderLeft: `5px solid ${designTokens.accent}`,
               display: 'flex',
               flexDirection: 'column',
               cursor: 'pointer',
               // HARDWARE ACCELERATED ANIMATIONS
               transition: 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94), box-shadow 0.3s ease, border-color 0.3s ease',
               transform: isCurrentHovered ? 'translateY(-6px)' : 'translateY(0)',
               boxShadow: isCurrentHovered ? '0 12px 24px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0,0,0,0.01)',
               borderColor: isCurrentHovered ? designTokens.goldLight : designTokens.accent
             };

             return (
               <div 
                 key={index}
                 style={cardStyle}
                 onMouseEnter={() => setHoveredCardIndex(index)}
                 onMouseLeave={() => setHoveredCardIndex(null)}
               >
                 {/* ICON CONTAINER */}
                 <div style={{ 
                   fontSize: '24px', 
                   backgroundColor: card.iconBg, 
                   display: 'flex', 
                   alignItems: 'center', 
                   justifyContent: 'center',
                   width: '48px', 
                   height: '48px', 
                   borderRadius: '50%', 
                   marginBottom: '20px' 
                 }}>
                   {card.iconEmoji}
                 </div>
                 
                 {/* CARD TEXT CONTENTS */}
                 <h3 style={{ ...styles.cardH3, color: designTokens.white, marginTop: 0 }}>
                   {card.title}
                 </h3>
                 
                 <p style={{ ...styles.cardP, color: '#94a3b8', margin: 0, textAlign: 'justify', textJustify: 'inter-word' }}>
                   {card.description}
                 </p>
               </div>
             );
           })}
         </div>
       </div>
     </section>

      {/* OPERATIONS WORKFLOW TIMELINE PIPELINE */}
      <section id="how" style={styles.howSection}>
        <div style={styles.sectionInner}>
          <div style={styles.sectionLabel}>How It Works</div>
          <h2 style={styles.sectionTitle}>
            Simple. Automatic. <em style={{ color: designTokens.gold, fontStyle: 'italic' }}>Foolproof.</em>
          </h2>
          <p style={styles.sectionDesc}>
            Once your facility is approved, BookPolicyGuard™ runs automatically on every order —
            <br/>
            no action required from your staff.
          </p>

          {/* VIDEO CONTAINER */}
          <div style={{ 
            margin: '50px auto 0 auto', 
            maxWidth: '900px', 
            width: '100%', 
            borderRadius: '16px', 
            overflow: 'hidden', 
            boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
            aspectRatio: '16 / 9',
            backgroundColor: '#000'
          }}>
            <video 
              width="100%" 
              height="100%" 
              controls
              preload="metadata"
              poster="/how-it-works-cover.png"
              style={{ display: 'block' }}
            >
              {/* Replace 'video-filename.mp4' with the name of your file in the public folder */}
              <source src="/BookPolicyGuard™.mp4" type="video/mp4" />
              {/*<source src="/How-it-works.mp4" type="video/mp4" />*/}
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      <PolicySimulator 
        simulatedFacility={simulatedFacility}
        setSimulatedFacility={setSimulatedFacility}
        simulatedBinding={simulatedBinding}
        setSimulatedBinding={setSimulatedBinding}
        simulatedResult={simulatedResult}
        setSimulatedResult={setSimulatedResult} // Add this line
      />

      {/* ACCORDION FAQ SYSTEM PIPELINE */}
      {/*<section id="faq" style={{ ...styles.problemSection, backgroundColor: '#ffffff', borderTop: `1px solid ${designTokens.offWhite}` }}>
        <div style={styles.sectionInner}>
          <SectionHeader 
            labelText="Common Questions"
            title="Frequently Reviewed Infrastructure Protocols"
            description="Understand the structural setup details governing automated compliance checks across public networks."
            lightMode={true}
          />
          <div style={{ maxWidth: '900px', margin: '0 auto', marginTop: '40px' }}>
            <AccordionItem question="Does mailroom staff require active application software setup?" answer="No. The configuration runs entirely native inside centralized distribution processing points. Your team receives audited items without managing hardware keys or endpoints locally." />
            <AccordionItem question="How fast do local state regulatory rules sync across data engines?" answer="Updates process instantly. Once facility compliance coordinators submit programmatic framework revisions, technical modules go live inside 15 minutes." />
            <AccordionItem question="What occurs if unverified sources attempt to dispatch catalog paths?" answer="Checkout logic maps security constraints instantly prior to generation. The booking portal cancels payment, preventing production overhead transparently." />
          </div>
        </div>
      </section>*/}
      <FAQSection />

      <RegistrationForm formSubmitted={formSubmitted} setFormSubmitted={setFormSubmitted} />

      {/* MASTER LOGISTICAL CLOSING BAR */}
      <footer style={styles.footer}>
        <div style={styles.footerContainer}>
          <div>
            <div style={styles.footerLogo}>Book<span style={{ color: designTokens.gold }}>Policy</span>Guard™</div>
            <p style={styles.footerNotice}>© 2026 SureShot Books Publishing LLC. Audit loops mapped logging security vector patterns under secure parameters.</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#94a3b8', fontSize: '14px' }}>
            <Mail size={18} color={designTokens.gray} /> <span>compliance@sureshotbooks.com</span>
          </div>
        </div>
      </footer>
      <ScrollToTop /> 
    </div>
  );
}